
import React, { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';

interface PasswordProtectionProps {
  onSuccess: () => void;
  password?: string;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ 
  onSuccess, 
  password = 'podsforever' 
}) => {
  const [inputPassword, setInputPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate a small delay for better UX
    setTimeout(() => {
      if (inputPassword === password) {
        onSuccess();
      } else {
        setError('Incorrect password. Please try again.');
        setInputPassword('');
      }
      setIsLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="bg-foreground/5 border border-foreground/10 rounded-2xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <Lock className="text-primary" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-foreground font-funnel mb-2">Protected Area</h1>
          <p className="text-muted-foreground font-sans">Enter the password to access this area</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={inputPassword}
              onChange={(e) => setInputPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 border border-foreground/15 rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary pr-12 font-sans text-foreground placeholder:text-muted-foreground"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center font-sans">{error}</div>
          )}

          <button
            type="submit"
            disabled={isLoading || !inputPassword.trim()}
            className="w-full bg-primary text-primary-foreground py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-sans font-medium"
          >
            {isLoading ? 'Verifying...' : 'Access Area'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordProtection;
